import React, { useContext, useState } from 'react';
import { View, ScrollView, StyleSheet } from 'react-native';
import { Avatar, Button, Card, Text, ActivityIndicator } from 'react-native-paper';
import { NativeBaseProvider } from 'native-base';
import { useMutation, useQuery } from '@apollo/client';
import { useToast } from 'react-native-toast-notifications';
import { AuthContext } from '../../context/AuthContext';
import { ACT_AS_CHILD } from '../../gql/user/mutation';
import { MyChildrenQuery } from '../../gql/user/query';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  header: {
    padding: 24,
    paddingTop: 48,
    alignItems: 'center',
  },
  title: {
    fontSize: 22,
    fontWeight: 'bold',
    marginBottom: 6,
  },
  subtitle: {
    fontSize: 14,
    color: '#666',
    textAlign: 'center',
  },
  list: {
    padding: 16,
    gap: 12,
  },
  card: {
    marginBottom: 12,
  },
  cardContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    paddingVertical: 4,
  },
  accountInfo: {
    flex: 1,
  },
  accountName: {
    fontSize: 16,
    fontWeight: '600',
  },
  accountSub: {
    fontSize: 13,
    color: '#666',
    marginTop: 2,
  },
});

function initials(firstName, lastName) {
  return `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase();
}

function AccountSelectorScreen() {
  const { authState, markAccountSelected, switchToChild, switchToParent } = useContext(AuthContext);
  const toast = useToast();
  const [loadingId, setLoadingId] = useState(null);

  // Fetch children for the current parent (or siblings, if already acting as one of them —
  // the resolver scopes to the caller's own parentId when set, see api/src/user/index.ts)
  const { data, loading } = useQuery(MyChildrenQuery, {
    fetchPolicy: 'network-only',
    onError: (e) => toast.show(e.message, { type: 'danger' }),
  });

  const [actAsChild] = useMutation(ACT_AS_CHILD, {
    onError: (e) => {
      toast.show(e.message);
      setLoadingId(null);
    },
  });

  const parentLabel = authState.isActingAsChild
    ? (authState.parentSession?.username ?? authState.parentSession?.email)
    : (authState.username ?? authState.email);

  const myChildren = data?.myChildren ?? [];

  const handleSelectParent = async () => {
    setLoadingId('parent');
    if (authState.isActingAsChild) {
      await switchToParent();
    } else {
      await markAccountSelected();
    }
  };

  const handleSelectChild = async (child) => {
    setLoadingId(child.id);
    // If currently acting as a child, restore parent session first so the
    // actAsChild mutation is called with the parent's token (which has the authority)
    if (authState.isActingAsChild) {
      await switchToParent();
    }
    const result = await actAsChild({ variables: { childId: child.id } });
    if (result?.data?.actAsChild) {
      await switchToChild(result.data.actAsChild);
    }
    setLoadingId(null);
  };

  if (loading) {
    return (
      <View style={[styles.container, { justifyContent: 'center', alignItems: 'center' }]}>
        <ActivityIndicator />
      </View>
    );
  }

  return (
    <ScrollView style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.title}>Quel compte utiliser ?</Text>
        <Text style={styles.subtitle}>
          Sélectionnez le compte avec lequel vous souhaitez continuer.
          {'\n'}Vous pourrez changer à tout moment depuis le menu.
        </Text>
        {authState.accountSelected === false && authState.authenticated && (
          <Button
            mode="text"
            compact
            onPress={() => markAccountSelected()}
            style={{ marginTop: 8 }}
          >
            Annuler
          </Button>
        )}
      </View>

      <View style={styles.list}>
        {/* Parent account */}
        <Card style={styles.card} onPress={() => handleSelectParent()}>
          <Card.Content style={styles.cardContent}>
            <Avatar.Text size={48} label={initials(...(authState.username ?? '').split(' '))} />
            <View style={styles.accountInfo}>
              <Text style={styles.accountName}>{parentLabel}</Text>
              <Text style={styles.accountSub}>Compte accompagnateur</Text>
            </View>
            {loadingId === 'parent' ? (
              <ActivityIndicator size={20} />
            ) : (
              <Button mode="outlined" compact onPress={handleSelectParent}>
                Choisir
              </Button>
            )}
          </Card.Content>
        </Card>

        {/* Child accounts */}
        {myChildren.map((child) => (
          <Card key={child.id} style={styles.card} onPress={() => handleSelectChild(child)}>
            <Card.Content style={styles.cardContent}>
              <Avatar.Text
                size={48}
                label={initials(child.firstName, child.lastName)}
                style={{ backgroundColor: '#1976d2' }}
              />
              <View style={styles.accountInfo}>
                <Text style={styles.accountName}>
                  {child.firstName} {child.lastName}
                </Text>
                <Text style={styles.accountSub}>Compte compétiteur</Text>
              </View>
              {loadingId === child.id ? (
                <ActivityIndicator size={20} />
              ) : (
                <Button mode="outlined" compact onPress={() => handleSelectChild(child)}>
                  Choisir
                </Button>
              )}
            </Card.Content>
          </Card>
        ))}
      </View>
    </ScrollView>
  );
}

export default function AccountSelectorScreenWrapper() {
  return (
    <NativeBaseProvider>
      <AccountSelectorScreen />
    </NativeBaseProvider>
  );
}
